import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Quote, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const SuccessStoriesPage = () => {
  const navigate = useNavigate();

  const stories = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      role: 'Cinematographer',
      department: 'Camera Department',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a',
      location: 'Mumbai, Maharashtra',
      quote: 'Shoot Base helped me connect with amazing production houses across India. Found my dream project within weeks!',
      story: 'After 8 years as a freelance cinematographer, I was struggling to find consistent work beyond my immediate network. Shoot Base changed everything. Within three weeks of creating my profile, I was contacted by a major production house in Bangalore for a feature film. The project visibility and verified badge gave me the credibility I needed.',
      project: 'Feature Film - "Echoes of Time"',
      rating: 5
    },
    {
      id: 2,
      name: 'Priya Sharma',
      role: 'Art Director',
      department: 'Art Department',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2',
      location: 'Delhi, NCR',
      quote: 'The platform made it so easy to showcase my portfolio and get hired for multiple feature films. Highly recommended!',
      story: 'As an art director, my portfolio is everything. Shoot Base gave me a professional space to showcase my work with high-quality images and detailed project descriptions. I\'ve received inquiries from production houses in 5 different cities and have been booked for back-to-back projects for the next 6 months.',
      project: 'Web Series - "Delhi Chronicles"',
      rating: 5
    },
    {
      id: 3,
      name: 'Arjun Menon',
      role: 'Sound Engineer',
      department: 'Sound Department',
      image: 'https://images.pexels.com/photos/29852852/pexels-photo-29852852.jpeg',
      location: 'Chennai, Tamil Nadu',
      quote: 'As a freelancer, Shoot Base became my go-to platform for finding quality projects. The community is incredibly supportive.',
      story: 'The film industry in Chennai is tight-knit, and breaking in as an outsider was tough. Shoot Base opened doors I didn\'t know existed. The location-based search meant local producers could find me easily, and the verification process gave them confidence in my skills. I\'ve worked on 12 projects in the past year thanks to this platform.',
      project: 'Documentary - "Sounds of the South"',
      rating: 5
    },
    {
      id: 4,
      name: 'Kavita Reddy',
      role: 'Production Manager',
      department: 'Production',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956',
      location: 'Hyderabad, Telangana',
      quote: 'Finding reliable crew for multi-city shoots was always a headache. Shoot Base made it seamless and efficient.',
      story: 'We were producing a web series that required shooting in 4 different cities. Using Shoot Base, I was able to hire verified professionals in each location without relying on word-of-mouth. The platform saved us weeks of coordination time and the quality of crew we got was exceptional.',
      project: 'Web Series Production',
      rating: 5
    },
    {
      id: 5,
      name: 'Vikram Singh',
      role: 'VFX Artist',
      department: 'SFX / VFX',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
      location: 'Bangalore, Karnataka',
      quote: 'The platform connected me with projects I would have never discovered otherwise. Game changer for VFX freelancers.',
      story: 'Most VFX work happens behind the scenes, and getting noticed was always a challenge. Shoot Base\'s portfolio feature allowed me to showcase my reel and technical skills. I\'ve been hired for projects ranging from commercials to feature films, all through the platform.',
      project: 'Feature Film VFX',
      rating: 5
    },
    {
      id: 6,
      name: 'Meera Patel',
      role: 'Makeup Artist',
      department: 'Makeup & Hair',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
      location: 'Ahmedabad, Gujarat',
      quote: 'From local shoots to national projects, Shoot Base helped me scale my career beyond my city.',
      story: 'I started as a local makeup artist doing wedding shoots and small productions. After joining Shoot Base and getting verified, I was approached for a national commercial shoot. That one project led to multiple offers, and now I work with some of the biggest production houses in India.',
      project: 'National Commercial Campaign',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              <Star className="w-3 h-3 mr-1" />
              Success Stories
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Real Stories from
              <span className="text-accent"> Real Professionals</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Hear how Shoot Base helped film professionals and production houses connect and create magic
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {stories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full bg-card border-border hover:border-accent/50 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    {/* Header with Image and Info */}
                    <div className="flex items-start gap-4 mb-4">
                      <img 
                        src={story.image} 
                        alt={story.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-accent/20"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground">{story.name}</h3>
                        <p className="text-sm text-muted-foreground">{story.role}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="text-xs bg-accent/10 text-accent border-accent/30">
                            {story.department}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {story.location}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex gap-1 mb-3">
                      {[...Array(story.rating)].map((_, i) => (
                        <Star key={`${story.id}-star-${i}`} className="w-4 h-4 fill-accent text-accent" />
                      ))}
                    </div>

                    {/* Quote */}
                    <div className="mb-4">
                      <Quote className="w-6 h-6 text-accent/30 mb-2" />
                      <p className="text-muted-foreground italic text-sm leading-relaxed">
                        "{story.quote}"
                      </p>
                    </div>

                    {/* Full Story */}
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {story.story}
                    </p>

                    {/* Project Info */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground pt-4 border-t border-border">
                      <Briefcase className="w-4 h-4 text-accent" />
                      <span className="font-medium">{story.project}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">Ready to Write Your Success Story?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of professionals who found their dream projects through Shoot Base
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold"
                onClick={() => navigate('/create-profile')}
              >
                Join as Professional
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-accent text-accent hover:bg-accent hover:text-primary-foreground"
                onClick={() => navigate('/post-project')}
              >
                Create a Hiring Post
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;